import { Plus, Target } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FY_OPTIONS, getCurrentFiscalQuarter } from "@/components/deal-metrics/shared";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatFY } from "@/lib/fiscal";
import { AffiliateGoalsSection } from "./affiliate-goals-section";
import { AffiliateMappingSection } from "./affiliate-mapping-section";
import { CampusAffiliatesSection } from "./campus-affiliates-section";
import { CampusBudgetSection } from "./campus-budget-section";
import { CampusTargetsSection } from "./campus-targets-section";
import { CreatedGoalsSection } from "./created-goals-section";
import { FPReportingGoalsSection } from "./fp-reporting-goals-section";
import { MetricGoalsSection } from "./metric-goals-section";
import { NonRelationalGoalsSection } from "./non-relational-goals-section";
import {
  AFF_METRIC_PREFIX,
  ALL_SECTION_TYPES,
  CAMPUS_METRICS,
  CREATED_METRIC_PREFIX,
  FP_METRIC_PREFIX,
  GOAL_SECTION_CONFIG,
  type GoalSectionType,
  NR_METRIC_PREFIX,
} from "./shared";
import { SourceGoalsSection } from "./source-goals-section";

export function GoalsAndTargets({
  ownerId,
  ownerName,
  initialSection,
  readOnly = false,
}: {
  ownerId: string;
  ownerName: string;
  initialSection?: string;
  readOnly?: boolean;
}) {
  const { fy: currentFY } = getCurrentFiscalQuarter();
  const [selectedFY, setSelectedFY] = useState(currentFY);
  const [dataPresence, setDataPresence] = useState<Record<GoalSectionType, boolean>>({
    "metric-goals": false,
    "non-relational-goals": false,
    "affiliate-goals": false,
    "source-goals": false,
    "campus-targets": false,
    "campus-affiliates": false,
    "campus-budget": false,
    "affiliate-mapping": false,
    "created-goals": false,
    "fp-reporting-goals": false,
  });
  const [temporarilyAdded, setTemporarilyAdded] = useState<Set<GoalSectionType>>(() => {
    const initial = new Set<GoalSectionType>();
    if (initialSection && ALL_SECTION_TYPES.includes(initialSection as GoalSectionType)) {
      initial.add(initialSection as GoalSectionType);
    }
    return initial;
  });
  const [initialCheckDone, setInitialCheckDone] = useState(false);

  // Check data presence on mount and FY change
  useEffect(() => {
    let cancelled = false;

    async function checkPresence() {
      try {
        const [targetsRes, setsRes, affiliatesRes, budgetRes, affMappingsRes, ownerRes] =
          await Promise.all([
            fetch(`/api/owners/${ownerId}/targets?fy=${selectedFY}`),
            fetch(`/api/owners/${ownerId}/source-goal-sets?fy=${selectedFY}`),
            fetch(`/api/owners/${ownerId}/campus-affiliates?fy=${selectedFY}`),
            fetch(`/api/owners/${ownerId}/campus-budget?fy=${selectedFY}`),
            fetch(`/api/owners/${ownerId}/affiliate-mappings?fy=${selectedFY}`),
            fetch(`/api/owners/${ownerId}`),
          ]);

        if (cancelled) return;

        const presence: Record<GoalSectionType, boolean> = {
          "metric-goals": false,
          "non-relational-goals": false,
          "affiliate-goals": false,
          "source-goals": false,
          "campus-targets": false,
          "campus-affiliates": false,
          "campus-budget": false,
          "affiliate-mapping": false,
          "created-goals": false,
          "fp-reporting-goals": false,
        };

        if (targetsRes.ok) {
          const data = await targetsRes.json();
          const targets = data.targets as { metric: string; targetValue: number }[];
          presence["metric-goals"] = targets.some(
            (t) =>
              t.metric !== CAMPUS_METRICS.leads &&
              t.metric !== CAMPUS_METRICS.dealsWon &&
              !t.metric.startsWith(NR_METRIC_PREFIX) &&
              !t.metric.startsWith(AFF_METRIC_PREFIX) &&
              !t.metric.startsWith(CREATED_METRIC_PREFIX) &&
              !t.metric.startsWith(FP_METRIC_PREFIX),
          );
          presence["non-relational-goals"] = targets.some((t) =>
            t.metric.startsWith(NR_METRIC_PREFIX),
          );
          presence["affiliate-goals"] = targets.some((t) => t.metric.startsWith(AFF_METRIC_PREFIX));
          presence["campus-targets"] = targets.some(
            (t) => t.metric === CAMPUS_METRICS.leads || t.metric === CAMPUS_METRICS.dealsWon,
          );
          presence["created-goals"] = targets.some((t) =>
            t.metric.startsWith(CREATED_METRIC_PREFIX),
          );
          presence["fp-reporting-goals"] = targets.some((t) =>
            t.metric.startsWith(FP_METRIC_PREFIX),
          );
        }

        if (setsRes.ok) {
          const data = await setsRes.json();
          presence["source-goals"] = (data.sourceGoalSets ?? []).length > 0;
        }

        if (affiliatesRes.ok) {
          const data = await affiliatesRes.json();
          presence["campus-affiliates"] = (data.campuses ?? []).length > 0;
        }

        if (budgetRes.ok) {
          const data = await budgetRes.json();
          presence["campus-budget"] = data.budget?.annualBudget != null;
        }

        if (affMappingsRes.ok) {
          const data = await affMappingsRes.json();
          const hasMappings = (data.mappings ?? []).length > 0;
          let hasBonusConfig = false;
          if (ownerRes.ok) {
            const ownerData = await ownerRes.json();
            hasBonusConfig =
              (ownerData.owner?.affiliateBonusPerDeal ?? 0) > 0 ||
              (ownerData.owner?.drAffiliateBonusPerDeal ?? 0) > 0;
          }
          presence["affiliate-mapping"] = hasMappings || hasBonusConfig;
        }

        setDataPresence(presence);
        setInitialCheckDone(true);
      } catch {
        setInitialCheckDone(true);
      }
    }

    checkPresence();
    // Clear temporarily added sections on FY change
    setTemporarilyAdded((_prev) => {
      if (initialSection && ALL_SECTION_TYPES.includes(initialSection as GoalSectionType)) {
        return new Set([initialSection as GoalSectionType]);
      }
      return new Set();
    });

    return () => {
      cancelled = true;
    };
  }, [ownerId, selectedFY, initialSection]);

  const handleDataChange = useCallback(
    (type: GoalSectionType) => (hasData: boolean) => {
      setDataPresence((prev) => ({ ...prev, [type]: hasData }));
      if (hasData) {
        setTemporarilyAdded((prev) => {
          const next = new Set(prev);
          next.delete(type);
          return next;
        });
      }
    },
    [],
  );

  // Stable per-section callbacks — avoids re-render loops from new function refs
  const onMetricGoalsChange = useMemo(() => handleDataChange("metric-goals"), [handleDataChange]);
  const onNonRelationalGoalsChange = useMemo(
    () => handleDataChange("non-relational-goals"),
    [handleDataChange],
  );
  const onAffiliateGoalsChange = useMemo(
    () => handleDataChange("affiliate-goals"),
    [handleDataChange],
  );
  const onSourceGoalsChange = useMemo(() => handleDataChange("source-goals"), [handleDataChange]);
  const onCampusTargetsChange = useMemo(
    () => handleDataChange("campus-targets"),
    [handleDataChange],
  );
  const onCampusAffiliatesChange = useMemo(
    () => handleDataChange("campus-affiliates"),
    [handleDataChange],
  );
  const onCampusBudgetChange = useMemo(() => handleDataChange("campus-budget"), [handleDataChange]);
  const onAffiliateMappingChange = useMemo(
    () => handleDataChange("affiliate-mapping"),
    [handleDataChange],
  );
  const onCreatedGoalsChange = useMemo(() => handleDataChange("created-goals"), [handleDataChange]);
  const onFPReportingGoalsChange = useMemo(
    () => handleDataChange("fp-reporting-goals"),
    [handleDataChange],
  );

  const removeSection = useCallback((type: GoalSectionType) => {
    setDataPresence((prev) => ({ ...prev, [type]: false }));
    setTemporarilyAdded((prev) => {
      const next = new Set(prev);
      next.delete(type);
      return next;
    });
  }, []);

  const onRemoveMetricGoals = useCallback(() => removeSection("metric-goals"), [removeSection]);
  const onRemoveNonRelationalGoals = useCallback(
    () => removeSection("non-relational-goals"),
    [removeSection],
  );
  const onRemoveAffiliateGoals = useCallback(
    () => removeSection("affiliate-goals"),
    [removeSection],
  );
  const onRemoveSourceGoals = useCallback(() => removeSection("source-goals"), [removeSection]);
  const onRemoveCampusTargets = useCallback(() => removeSection("campus-targets"), [removeSection]);
  const onRemoveCampusAffiliates = useCallback(
    () => removeSection("campus-affiliates"),
    [removeSection],
  );
  const onRemoveCampusBudget = useCallback(() => removeSection("campus-budget"), [removeSection]);
  const onRemoveAffiliateMapping = useCallback(
    () => removeSection("affiliate-mapping"),
    [removeSection],
  );
  const onRemoveCreatedGoals = useCallback(() => removeSection("created-goals"), [removeSection]);
  const onRemoveFPReportingGoals = useCallback(
    () => removeSection("fp-reporting-goals"),
    [removeSection],
  );

  function addSection(type: GoalSectionType) {
    setTemporarilyAdded((prev) => new Set(prev).add(type));
  }

  const visibleSections = ALL_SECTION_TYPES.filter(
    (type) => dataPresence[type] || temporarilyAdded.has(type),
  );

  const addableSections = ALL_SECTION_TYPES.filter(
    (type) => !dataPresence[type] && !temporarilyAdded.has(type),
  );

  if (!initialCheckDone) return null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-base font-semibold">
          <Target className="h-4 w-4 text-muted-foreground" />
          Goals & Targets
        </h2>
        <div className="flex items-center gap-2">
          {!readOnly && addableSections.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 text-xs">
                  <Plus className="h-3.5 w-3.5 mr-1.5" />
                  Add
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {addableSections.map((type) => (
                  <DropdownMenuItem key={type} onClick={() => addSection(type)}>
                    {GOAL_SECTION_CONFIG[type].label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <Select value={String(selectedFY)} onValueChange={(v) => setSelectedFY(Number(v))}>
            <SelectTrigger className="w-[120px] h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FY_OPTIONS.map((fy) => (
                <SelectItem key={fy} value={String(fy)}>
                  {formatFY(fy)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {visibleSections.length === 0 ? (
        <div className="rounded-lg border border-dashed p-8 text-center">
          <p className="text-sm text-muted-foreground mb-3">
            No goals or targets configured for {formatFY(selectedFY)}.
          </p>
          {!readOnly && addableSections.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Plus className="h-3.5 w-3.5 mr-1.5" />
                  Add Goal or Target
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {addableSections.map((type) => (
                  <DropdownMenuItem key={type} onClick={() => addSection(type)}>
                    {GOAL_SECTION_CONFIG[type].label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {visibleSections.includes("metric-goals") && (
            <MetricGoalsSection
              ownerId={ownerId}
              fiscalYear={selectedFY}
              onDataChange={onMetricGoalsChange}
              onRemove={readOnly ? undefined : onRemoveMetricGoals}
              readOnly={readOnly}
            />
          )}
          {visibleSections.includes("non-relational-goals") && (
            <NonRelationalGoalsSection
              ownerId={ownerId}
              fiscalYear={selectedFY}
              onDataChange={onNonRelationalGoalsChange}
              onRemove={readOnly ? undefined : onRemoveNonRelationalGoals}
              readOnly={readOnly}
            />
          )}
          {visibleSections.includes("affiliate-goals") && (
            <AffiliateGoalsSection
              ownerId={ownerId}
              fiscalYear={selectedFY}
              onDataChange={onAffiliateGoalsChange}
              onRemove={readOnly ? undefined : onRemoveAffiliateGoals}
              readOnly={readOnly}
            />
          )}
          {visibleSections.includes("source-goals") && (
            <SourceGoalsSection
              ownerId={ownerId}
              fiscalYear={selectedFY}
              onDataChange={onSourceGoalsChange}
              onRemove={readOnly ? undefined : onRemoveSourceGoals}
              readOnly={readOnly}
            />
          )}
          {visibleSections.includes("campus-targets") && (
            <CampusTargetsSection
              ownerId={ownerId}
              fiscalYear={selectedFY}
              onDataChange={onCampusTargetsChange}
              onRemove={readOnly ? undefined : onRemoveCampusTargets}
              readOnly={readOnly}
            />
          )}
          {visibleSections.includes("campus-affiliates") && (
            <CampusAffiliatesSection
              ownerId={ownerId}
              ownerName={ownerName}
              fiscalYear={selectedFY}
              onDataChange={onCampusAffiliatesChange}
              onRemove={readOnly ? undefined : onRemoveCampusAffiliates}
              readOnly={readOnly}
            />
          )}
          {visibleSections.includes("campus-budget") && (
            <CampusBudgetSection
              ownerId={ownerId}
              fiscalYear={selectedFY}
              onDataChange={onCampusBudgetChange}
              onRemove={readOnly ? undefined : onRemoveCampusBudget}
              readOnly={readOnly}
            />
          )}
          {visibleSections.includes("affiliate-mapping") && (
            <AffiliateMappingSection
              ownerId={ownerId}
              fiscalYear={selectedFY}
              onDataChange={onAffiliateMappingChange}
              onRemove={readOnly ? undefined : onRemoveAffiliateMapping}
              readOnly={readOnly}
            />
          )}
          {visibleSections.includes("created-goals") && (
            <CreatedGoalsSection
              ownerId={ownerId}
              fiscalYear={selectedFY}
              onDataChange={onCreatedGoalsChange}
              onRemove={readOnly ? undefined : onRemoveCreatedGoals}
              readOnly={readOnly}
            />
          )}
          {visibleSections.includes("fp-reporting-goals") && (
            <FPReportingGoalsSection
              ownerId={ownerId}
              fiscalYear={selectedFY}
              onDataChange={onFPReportingGoalsChange}
              onRemove={readOnly ? undefined : onRemoveFPReportingGoals}
              readOnly={readOnly}
            />
          )}
        </div>
      )}
    </div>
  );
}
